import React from 'react';

/**
 * Parse semi-structured text with embedded labels like "Productivity:" or "Benefits:"
 * into structured blocks with bold headings and content paragraphs.
 */
function parseRichText(text) {
  if (!text) return [];

  // Split on patterns like "LabelWord:" or "Label Words:" at the start of a line or after a newline
  const parts = text.split(/\n/).filter(line => line.trim());
  const blocks = [];

  for (const line of parts) {
    // Check if line starts with a label pattern like "Productivity:" or "Advanced Features:"
    const labelMatch = line.match(/^([A-Z][A-Za-z\s]+):\s*(.*)$/);
    if (labelMatch) {
      blocks.push({ type: 'labeled', label: labelMatch[1].trim(), content: labelMatch[2].trim() });
    } else {
      blocks.push({ type: 'text', content: line.trim() });
    }
  }

  return blocks;
}

export default function OverviewSection({ product }) {
  // Fallback chain: overview → shortDescPgmn → descriptionPGMJ → descriptionPGMN
  const overviewText = product?.overview || product?.shortDescPgmn || product?.descriptionPGMJ || product?.descriptionPGMN || '';
  const keyFeatures = product?.keyFeatures;

  const hasOverview = overviewText.trim().length > 0;
  const hasKeyFeatures =
    keyFeatures &&
    ((typeof keyFeatures === 'string' && keyFeatures.trim().length > 0) ||
      (Array.isArray(keyFeatures) && keyFeatures.length > 0));

  if (!hasOverview && !hasKeyFeatures) {
    return (
      <p className="text-n5 italic py-4 px-4 font-sandvik-text">
        No overview available for this product.
      </p>
    );
  }

  // Parse key features with rich text formatting
  const featureBlocks = hasKeyFeatures && typeof keyFeatures === 'string'
    ? parseRichText(keyFeatures)
    : [];

  return (
    <div className="py-4 px-4">
      {/* Overview text */}
      {hasOverview &&
        overviewText.split('\n').filter(l => l.trim()).map((line, idx) => (
          <p key={idx} className="text-n7 font-sandvik-text leading-relaxed mb-3">
            {line}
          </p>
        ))}

      {/* Key features — rich text formatted */}
      {featureBlocks.length > 0 && (
        <div className="mt-4 space-y-3">
          {featureBlocks.map((block, idx) => (
            <div key={idx}>
              {block.type === 'labeled' ? (
                <p className="text-n7 font-sandvik-text leading-relaxed">
                  <span className="font-normal text-carbon-black">{block.label}: </span>
                  {block.content}
                </p>
              ) : (
                <p className="text-n7 font-sandvik-text leading-relaxed">
                  {block.content}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Key features as array */}
      {hasKeyFeatures && Array.isArray(keyFeatures) && (
        <ul className="list-disc list-inside py-2 space-y-1 text-n7 font-sandvik-text mt-3">
          {keyFeatures.map((feature, idx) => (
            <li key={idx}>{feature}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
